import type { SuburbList, SuburbListItem } from "@repo/types/suburb-list";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/alert-dialog";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Progress } from "@repo/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Filter,
  List,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import type { SuburbRecord as Suburb } from "@/lib/api";
import {
  addSuburbsToList,
  createSuburbList,
  deleteSuburbList,
  fetchSuburbListItems,
  fetchSuburbLists,
  fetchSuburbs,
  removeSuburbFromList,
  updateSuburbList,
} from "@/lib/api";

const STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const PAGE_SIZE = 50;
const BATCH_SIZE = 500;

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function getStateBadgeColor(state: string): string {
  const colors: Record<string, string> = {
    VIC: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    NSW: "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300",
    QLD: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
    WA: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    SA: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    TAS: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    NT: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
    ACT: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  };
  return colors[state] || "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
}

/** Aggregate state counts from a list of suburb items. */
function getStateCounts(items: SuburbListItem[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) {
    const state = item.suburbs?.state;
    if (state) {
      counts[state] = (counts[state] ?? 0) + 1;
    }
  }
  return counts;
}

// ─── Suburb Lists Section ────────────────────────────────────────────────

interface SuburbListCardProps {
  list: SuburbList;
  onDelete: (id: string) => void;
  onUpdate: (id: string, name: string, description: string) => void;
}

function SuburbListCard({ list, onDelete, onUpdate }: SuburbListCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState<SuburbListItem[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(list.name);
  const [editDescription, setEditDescription] = useState(list.description ?? "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Add-suburb search state. Existing items in the list are filtered out
  // so the user doesn't see candidates already in the list.
  const [addSearch, setAddSearch] = useState("");
  const [addCandidates, setAddCandidates] = useState<Suburb[]>([]);
  const [isAddSearching, setIsAddSearching] = useState(false);
  const [addingSuburbIds, setAddingSuburbIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && items.length === 0) {
      try {
        setIsLoadingItems(true);
        const data = await fetchSuburbListItems(list.id);
        setItems(data);
      } catch (_err) {
        toast({ title: "Error", description: "Failed to load list items", variant: "destructive" });
      } finally {
        setIsLoadingItems(false);
      }
    }
  };

  const handleRemoveItem = async (suburbId: string) => {
    try {
      await removeSuburbFromList(list.id, suburbId);
      setItems((prev) => prev.filter((i) => i.suburb_id !== suburbId));
      toast({ title: "Removed", description: "Suburb removed from list" });
    } catch (_err) {
      toast({ title: "Error", description: "Failed to remove suburb", variant: "destructive" });
    }
  };

  const handleAddSuburb = async (suburb: Suburb) => {
    if (addingSuburbIds.has(suburb.id)) return;
    setAddingSuburbIds((prev) => new Set(prev).add(suburb.id));
    try {
      await addSuburbsToList(list.id, [suburb.id]);
      const item: SuburbListItem = {
        list_id: list.id,
        suburb_id: suburb.id,
        suburbs: {
          locality: suburb.locality,
          suburb_name: suburb.suburb_name,
          postcode: suburb.postcode,
          state: suburb.state,
          region: suburb.region ?? null,
        },
      } as SuburbListItem;
      setItems((prev) => [...prev, item]);
      setAddCandidates((prev) => prev.filter((s) => s.id !== suburb.id));
      toast({
        title: "Added",
        description: `${suburb.locality ?? suburb.suburb_name} added to list`,
      });
    } catch (_err) {
      toast({
        title: "Error",
        description: "Failed to add suburb",
        variant: "destructive",
      });
    } finally {
      setAddingSuburbIds((prev) => {
        const next = new Set(prev);
        next.delete(suburb.id);
        return next;
      });
    }
  };

  // Debounced suburb-search effect: hits the suburbs table when the user
  // types ≥2 chars, then strips out any already in the list.
  useEffect(() => {
    if (!expanded) return;
    const q = addSearch.trim();
    if (q.length < 2) {
      setAddCandidates([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        setIsAddSearching(true);
        const { data } = await fetchSuburbs({ search: q, limit: 20, offset: 0 });
        const existingIds = new Set(items.map((i) => i.suburb_id));
        setAddCandidates(data.filter((s) => !existingIds.has(s.id)));
      } catch (_err) {
        setAddCandidates([]);
      } finally {
        setIsAddSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [addSearch, items, expanded]);

  const handleSaveEdit = () => {
    onUpdate(list.id, editName, editDescription);
    setIsEditing(false);
  };

  const stateCounts = expanded ? getStateCounts(items) : {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-8 text-sm font-semibold"
                />
                <Input
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="h-8 text-sm"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit} disabled={!editName.trim()}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <CardTitle className="text-base flex items-center gap-2">
                  <List className="w-4 h-4 text-muted-foreground" />
                  {list.name}
                </CardTitle>
                {list.description && (
                  <CardDescription className="mt-1 text-xs">{list.description}</CardDescription>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="secondary" className="text-xs">
              {list.item_count ?? 0} suburbs
            </Badge>
            {!isEditing && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    setEditName(list.name);
                    setEditDescription(list.description ?? "");
                    setIsEditing(true);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleExpand}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-3">
          {/* Add-suburb search */}
          <div className="space-y-2">
            <Input
              placeholder="Add suburb… (type at least 2 letters)"
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              className="h-8 text-sm"
            />
            {(addCandidates.length > 0 || isAddSearching) && (
              <div className="rounded-md border max-h-48 overflow-y-auto">
                {isAddSearching && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {addCandidates.map((s) => {
                  const adding = addingSuburbIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={adding}
                      onClick={() => handleAddSuburb(s)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-muted text-left disabled:opacity-50"
                    >
                      <span>
                        <span className="font-medium">
                          {s.locality ?? s.suburb_name}
                        </span>{" "}
                        <span className="text-muted-foreground">
                          {s.state} {s.postcode}
                        </span>
                      </span>
                      {adding ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <span className="text-primary">+ Add</span>
                      )}
                    </button>
                  );
                })}
                {!isAddSearching && addCandidates.length === 0 && addSearch.trim().length >= 2 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    No suburbs found (or already in list)
                  </p>
                )}
              </div>
            )}
          </div>

          {isLoadingItems ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No suburbs in this list yet.</p>
          ) : (
            <div className="space-y-3">
              {/* State breakdown badges */}
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(stateCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([state, count]) => (
                    <Badge key={state} className={getStateBadgeColor(state)}>
                      {state}: {count}
                    </Badge>
                  ))}
              </div>

              {/* Suburb items table */}
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Suburb</TableHead>
                      <TableHead>Postcode</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.suburb_id}>
                        <TableCell className="font-medium text-sm">
                          {item.suburbs?.locality ?? item.suburbs?.suburb_name ?? item.suburb_id}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                            {item.suburbs?.postcode ?? "-"}
                          </code>
                        </TableCell>
                        <TableCell>
                          {item.suburbs?.state ? (
                            <Badge className={getStateBadgeColor(item.suburbs.state)}>
                              {item.suburbs.state}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {item.suburbs?.region ?? "-"}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveItem(item.suburb_id)}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{list.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this suburb list and its {list.item_count ?? 0} suburb
              assignments. The suburbs themselves will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(list.id);
                setShowDeleteDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete List
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Create List Dialog ──────────────────────────────────────────────────

interface CreateListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function CreateListDialog({ open, onOpenChange, onCreated }: CreateListDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      setIsCreating(true);
      await createSuburbList(name.trim(), description.trim() || undefined);
      toast({ title: "Created", description: `Suburb list "${name}" created` });
      setName("");
      setDescription("");
      onOpenChange(false);
      onCreated();
    } catch (_err) {
      toast({ title: "Error", description: "Failed to create list", variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Suburb List</DialogTitle>
          <DialogDescription>
            Create a reusable set of suburbs that can be assigned to scraping jobs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="list-name">Name</Label>
            <Input
              id="list-name"
              placeholder="e.g. Melbourne Inner 50"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="list-description">Description (optional)</Label>
            <Input
              id="list-description"
              placeholder="e.g. Inner Melbourne suburbs for weekly monitoring"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleCreate} disabled={!name.trim() || isCreating}>
            {isCreating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────

const Suburbs = () => {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();

  // Suburb lists state
  const [suburbLists, setSuburbLists] = useState<SuburbList[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Suburbs table state
  const [suburbs, setSuburbs] = useState<Suburb[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [isClearing, setIsClearing] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [postcodeFilter, setPostcodeFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const filterSignature = `${selectedState}|${postcodeFilter}|${searchQuery}`;

  // ── Load suburb lists ──
  const loadLists = useCallback(async () => {
    try {
      setIsLoadingLists(true);
      const data = await fetchSuburbLists();
      setSuburbLists(data);
    } catch (_err) {
      toast({ title: "Error", description: "Failed to load suburb lists", variant: "destructive" });
    } finally {
      setIsLoadingLists(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const handleDeleteList = async (listId: string) => {
    try {
      await deleteSuburbList(listId);
      setSuburbLists((prev) => prev.filter((l) => l.id !== listId));
      toast({ title: "Deleted", description: "Suburb list deleted" });
    } catch (_err) {
      toast({ title: "Error", description: "Failed to delete list", variant: "destructive" });
    }
  };

  const handleUpdateList = async (listId: string, name: string, description: string) => {
    try {
      const updated = await updateSuburbList(listId, {
        name,
        description: description || undefined,
      });
      setSuburbLists((prev) => prev.map((l) => (l.id === listId ? { ...l, ...updated } : l)));
      toast({ title: "Updated", description: "Suburb list updated" });
    } catch (_err) {
      toast({ title: "Error", description: "Failed to update list", variant: "destructive" });
    }
  };

  // ── Load suburbs table ──
  const loadSuburbs = useCallback(async () => {
    try {
      setIsLoading(true);
      const offset = (currentPage - 1) * PAGE_SIZE;
      const { data, count } = await fetchSuburbs({
        state: selectedState !== "all" ? selectedState : undefined,
        postcode: postcodeFilter || undefined,
        search: searchQuery || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setSuburbs(data);
      setTotalCount(count);
    } catch (_err) {
      toast({ title: "Error", description: "Failed to load suburbs", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, selectedState, postcodeFilter, searchQuery, toast]);

  useEffect(() => {
    loadSuburbs();
  }, [loadSuburbs]);
  useEffect(() => {
    if (filterSignature) {
      setCurrentPage(1);
    }
  }, [filterSignature]);

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setImportProgress(0);
      setImportStatus("Fetching CSV file...");

      const response = await fetch("/australian_postcodes.csv");
      if (!response.ok) throw new Error("Failed to fetch CSV file");

      const csvText = await response.text();
      const lines = csvText.split("\n").filter((line) => line.trim());

      setImportStatus("Parsing CSV data...");
      const header = parseCSVLine(lines[0]);
      const colIndex = {
        id: header.indexOf("id"),
        postcode: header.indexOf("postcode"),
        locality: header.indexOf("locality"),
        state: header.indexOf("state"),
        long: header.indexOf("long"),
        lat: header.indexOf("lat"),
        sa3name: header.indexOf("sa3name"),
        sa4name: header.indexOf("sa4name"),
      };

      const suburbsData: Array<{
        id: string;
        suburb_name: string;
        locality: string;
        postcode: string;
        state: string;
        region: string | null;
        latitude: number | null;
        longitude: number | null;
      }> = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length < 6) continue;
        const id = values[colIndex.id] || "";
        const locality = values[colIndex.locality] || "";
        const state = values[colIndex.state] || "";
        const postcode = values[colIndex.postcode] || "";
        if (!id || !locality || !state || !postcode) continue;
        const lat = values[colIndex.lat];
        const long = values[colIndex.long];
        const sa3name = colIndex.sa3name >= 0 ? values[colIndex.sa3name] : "";
        const sa4name = colIndex.sa4name >= 0 ? values[colIndex.sa4name] : "";

        suburbsData.push({
          id,
          suburb_name: locality,
          locality,
          postcode,
          state,
          region: sa3name || sa4name || null,
          latitude: lat ? parseFloat(lat) : null,
          longitude: long ? parseFloat(long) : null,
        });
      }

      setImportStatus(`Importing ${suburbsData.length.toLocaleString()} suburbs...`);
      let inserted = 0;
      let errors = 0;
      const totalBatches = Math.ceil(suburbsData.length / BATCH_SIZE);

      for (let i = 0; i < suburbsData.length; i += BATCH_SIZE) {
        const batch = suburbsData.slice(i, i + BATCH_SIZE);
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;
        setImportStatus(`Inserting batch ${batchNum} of ${totalBatches}...`);
        setImportProgress(Math.round((i / suburbsData.length) * 100));

        const { error } = await (supabase as any)
          .from("suburbs")
          .upsert(batch, { onConflict: "id" });

        if (error) {
          console.error(`Batch ${batchNum} error:`, error);
          errors++;
        } else {
          inserted += batch.length;
        }
      }

      setImportProgress(100);
      setImportStatus("");
      toast({
        title: "Import Complete",
        description: `Successfully imported ${inserted.toLocaleString()} suburbs${errors > 0 ? ` (${errors} batch errors)` : ""}`,
      });
      loadSuburbs();
    } catch (error) {
      toast({
        title: "Import Error",
        description: error instanceof Error ? error.message : "Failed to import suburbs",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
      setImportProgress(0);
      setImportStatus("");
    }
  };

  const handleClear = async () => {
    try {
      setIsClearing(true);
      const { error } = await (supabase as any).from("suburbs").delete().neq("id", "");
      if (error) throw error;
      toast({ title: "Cleared", description: "All suburbs have been removed" });
      loadSuburbs();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to clear suburbs",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
      setShowClearDialog(false);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Suburbs</h1>
          <p className="text-muted-foreground">
            Australian suburbs database and canonical suburb lists
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="py-1.5">
            <MapPin className="w-3.5 h-3.5 mr-1" />
            {totalCount.toLocaleString()} suburbs
          </Badge>
        </div>
      </div>

      {/* ── Suburb Lists Section ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <List className="w-4 h-4" />
                Suburb Lists
              </CardTitle>
              <CardDescription>Reusable suburb sets for scraping jobs</CardDescription>
            </div>
            {canManageJobs && (
              <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-1" />
                New List
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingLists ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : suburbLists.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No suburb lists yet. Create one to group suburbs for reuse across jobs.
            </p>
          ) : (
            <div className="space-y-3">
              {suburbLists.map((list) => (
                <SuburbListCard
                  key={list.id}
                  list={list}
                  onDelete={handleDeleteList}
                  onUpdate={handleUpdateList}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateListDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={loadLists}
      />

      {/* ── Data Management ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Data Management</CardTitle>
          <CardDescription>Import or manage Australian suburb data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {canManageJobs && (
              <>
                <Button onClick={handleImport} disabled={isImporting || isClearing}>
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {isImporting ? "Importing..." : "Import from CSV"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowClearDialog(true)}
                  disabled={isImporting || isClearing || totalCount === 0}
                >
                  {isClearing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Clear All
                </Button>
              </>
            )}
            <Button variant="outline" onClick={loadSuburbs} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {isImporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{importStatus}</span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Filters ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search suburb name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Postcode..."
              value={postcodeFilter}
              onChange={(e) => setPostcodeFilter(e.target.value)}
              className="w-[120px]"
              maxLength={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Suburbs Table ── */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Suburb</TableHead>
                <TableHead>Postcode</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">Coordinates</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : suburbs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {totalCount === 0 ? (
                      <div className="space-y-2">
                        <MapPin className="w-8 h-8 mx-auto opacity-50" />
                        <p>No suburbs imported yet</p>
                        <p className="text-sm">
                          Click "Import from CSV" to load Australian suburbs
                        </p>
                      </div>
                    ) : (
                      "No suburbs match your filters"
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                suburbs.map((suburb) => {
                  const localityName = suburb.locality || suburb.suburb_name;
                  return (
                  <TableRow key={suburb.id}>
                    <TableCell className="font-medium">
                      {localityName ? (
                        <Link
                          to={`/dashboard/suburbs/${suburb.id}`}
                          className="text-primary hover:underline"
                        >
                          {localityName}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                        {suburb.postcode}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStateBadgeColor(suburb.state)}>{suburb.state}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {suburb.region || "-"}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground font-mono">
                      {suburb.latitude && suburb.longitude
                        ? `${suburb.latitude.toFixed(4)}, ${suburb.longitude.toFixed(4)}`
                        : "-"}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}{" "}
                suburbs
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || isLoading}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clear Confirmation Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Suburbs?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {totalCount.toLocaleString()} suburbs from the
              database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Suburbs;
